/**
 * Window Manager Service
 * Manages all open detached trader windows across the trading application
 *
 * Features:
 * - Window registry and lifecycle management
 * - Window metadata persistence to localStorage
 * - Event emission for window lifecycle events
 * - Sync status tracking between main workspace and detached windows
 * - Crash detection and recovery
 */

/**
 * Window position coordinates
 */
export interface WindowPosition {
  x: number;
  y: number;
}

/**
 * Window size dimensions
 */
export interface WindowSize {
  width: number;
  height: number;
}

/**
 * Type of trader window
 */
export type TraderType = 'stock' | 'options' | 'futures' | 'forex' | 'crypto' | 'bond' | 'etf';

/**
 * Sync status of trader between main workspace and detached window
 */
export type SyncStatus = 'synced' | 'pending' | 'error';

/**
 * Reference to a detached trader window
 */
export interface WindowReference {
  traderId: string;
  type: TraderType;
  window: Window;
  position: WindowPosition;
  size: WindowSize;
  syncStatus: SyncStatus;
  openedAt: number;
  isFocused: boolean;
}

/**
 * Window metadata persisted to localStorage
 */
export interface WindowMetadata {
  traderId: string;
  type: TraderType;
  position: WindowPosition;
  size: WindowSize;
  openedAt: number;
}

/**
 * Window event listener callback signature
 */
export type WindowEventListener = (reference: WindowReference) => void;

/**
 * Focus tracking for window
 */
interface WindowFocusTracker {
  traderId: string;
  timer: ReturnType<typeof setTimeout> | null;
}

/**
 * Event emitter for window lifecycle events
 */
class WindowEventEmitter {
  private listeners: Map<string, Set<WindowEventListener>> = new Map();

  on(event: string, listener: WindowEventListener): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(listener);
  }

  off(event: string, listener: WindowEventListener): void {
    if (this.listeners.has(event)) {
      this.listeners.get(event)!.delete(listener);
    }
  }

  emit(event: string, reference: WindowReference): void {
    if (this.listeners.has(event)) {
      this.listeners.get(event)!.forEach(listener => listener(reference));
    }
  }

  clear(): void {
    this.listeners.clear();
  }
}

/**
 * Window Manager
 * Singleton service for managing detached trader windows
 */
export class WindowManager {
  private static instance: WindowManager;
  private windowRegistry: Map<string, WindowReference> = new Map();
  private hiddenTraders: Set<string> = new Set();
  private eventEmitter: WindowEventEmitter = new WindowEventEmitter();
  private focusTracker: WindowFocusTracker | null = null;
  private crashCheckInterval: ReturnType<typeof setTimeout> | null = null;
  private readonly STORAGE_KEY = 'trader-windows';
  private readonly CRASH_CHECK_INTERVAL = 5000; // 5 seconds

  private constructor() {
    this.initializeFromStorage();
    this.startCrashDetection();
  }

  /**
   * Get singleton instance of WindowManager
   */
  static getInstance(): WindowManager {
    if (!WindowManager.instance) {
      WindowManager.instance = new WindowManager();
    }
    return WindowManager.instance;
  }

  /**
   * Initialize window registry from localStorage
   */
  private initializeFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const metadata: WindowMetadata[] = JSON.parse(stored);
        // Note: We don't restore actual windows, just metadata for reference
        // Actual windows would be recreated by the application
      }
    } catch (error) {
      console.error('Failed to load window metadata from storage:', error);
    }
  }

  /**
   * Start periodic crash detection
   */
  private startCrashDetection(): void {
    if (this.crashCheckInterval) {
      clearInterval(this.crashCheckInterval);
    }

    this.crashCheckInterval = setInterval(() => {
      this.detectClosedWindows();
    }, this.CRASH_CHECK_INTERVAL);
  }

  /**
   * Stop crash detection
   */
  private stopCrashDetection(): void {
    if (this.crashCheckInterval) {
      clearInterval(this.crashCheckInterval);
      this.crashCheckInterval = null;
    }
  }

  /**
   * Detect and clean up closed windows
   */
  private detectClosedWindows(): void {
    const closedTraders: string[] = [];

    this.windowRegistry.forEach((reference, traderId) => {
      try {
        // Check if window is still accessible
        if (reference.window.closed) {
          closedTraders.push(traderId);
        }
      } catch {
        // Window was closed or security restriction
        closedTraders.push(traderId);
      }
    });

    closedTraders.forEach(traderId => {
      this.handleWindowClosed(traderId);
    });
  }

  /**
   * Handle window close - cleanup and emit event
   */
  private handleWindowClosed(traderId: string): void {
    const reference = this.windowRegistry.get(traderId);
    if (reference) {
      this.windowRegistry.delete(traderId);
      this.hiddenTraders.delete(traderId);
      this.persistWindowState();

      // Emit close event
      this.eventEmitter.emit('onWindowClose', reference);
    }
  }

  /**
   * Open a detached trader window
   *
   * @param traderId - Unique identifier for the trader
   * @param traderType - Type of trader (stock, options, etc.)
   * @param position - Optional window position
   * @param size - Optional window size
   * @returns Window reference or null if window couldn't be opened
   */
  openTraderWindow(
    traderId: string,
    traderType: TraderType,
    position?: WindowPosition,
    size?: WindowSize
  ): WindowReference | null {
    // Check if window already exists
    if (this.windowRegistry.has(traderId)) {
      const existing = this.windowRegistry.get(traderId)!;
      try {
        if (!existing.window.closed) {
          existing.window.focus();
          return existing;
        }
      } catch {
        // Window is closed, remove from registry
        this.windowRegistry.delete(traderId);
      }
    }

    // Default position and size
    const finalPosition: WindowPosition = position || {
      x: window.screenX + 100,
      y: window.screenY + 100
    };

    const finalSize: WindowSize = size || {
      width: 1200,
      height: 700
    };

    // Build window features string
    const windowFeatures = [
      `width=${finalSize.width}`,
      `height=${finalSize.height}`,
      `left=${finalPosition.x}`,
      `top=${finalPosition.y}`,
      'menubar=no',
      'toolbar=no',
      'location=no',
      'status=no',
      'resizable=yes',
      'scrollbars=yes'
    ].join(',');

    try {
      // Open the window with trader URL
      const traderUrl = `/trader?detached=true&traderId=${encodeURIComponent(traderId)}&type=${encodeURIComponent(traderType)}`;
      const newWindow = window.open(traderUrl, `trader-${traderId}`, windowFeatures);

      if (!newWindow) {
        console.error(`Failed to open window for trader ${traderId}`);
        return null;
      }

      // Create reference
      const reference: WindowReference = {
        traderId,
        type: traderType,
        window: newWindow,
        position: finalPosition,
        size: finalSize,
        syncStatus: 'pending',
        openedAt: Date.now(),
        isFocused: true
      };

      // Store in registry
      this.windowRegistry.set(traderId, reference);

      // Remove from hidden set if it was there
      this.hiddenTraders.delete(traderId);

      // Set up window close listener
      this.setupWindowCloseListener(newWindow, traderId);

      // Persist state
      this.persistWindowState();

      // Emit open event
      this.eventEmitter.emit('onWindowOpen', reference);

      return reference;
    } catch (error) {
      console.error(`Error opening window for trader ${traderId}:`, error);
      return null;
    }
  }

  /**
   * Set up close listener for a window
   */
  private setupWindowCloseListener(windowRef: Window, traderId: string): void {
    try {
      windowRef.addEventListener('beforeunload', () => {
        this.handleWindowClosed(traderId);
      });

      windowRef.addEventListener('unload', () => {
        this.handleWindowClosed(traderId);
      });
    } catch {
      // Cross-origin or other security restrictions
      // Crash detection will handle this
    }
  }

  /**
   * Close a detached trader window
   *
   * @param traderId - Trader identifier to close
   * @returns true if window was closed, false if not found
   */
  closeTraderWindow(traderId: string): boolean {
    const reference = this.windowRegistry.get(traderId);

    if (!reference) {
      return false;
    }

    try {
      reference.window.close();
    } catch {
      // Window might already be closed
    }

    this.handleWindowClosed(traderId);
    return true;
  }

  /**
   * Get all open windows
   *
   * @returns Array of window references
   */
  getOpenWindows(): WindowReference[] {
    return Array.from(this.windowRegistry.values());
  }

  /**
   * Get window reference for a specific trader
   *
   * @param traderId - Trader identifier
   * @returns Window reference or null if not found
   */
  getWindowForTrader(traderId: string): WindowReference | null {
    return this.windowRegistry.get(traderId) || null;
  }

  /**
   * Check if a trader is in a detached window
   *
   * @param traderId - Trader identifier
   * @returns true if trader has detached window, false otherwise
   */
  isTraderDetached(traderId: string): boolean {
    const reference = this.windowRegistry.get(traderId);

    if (!reference) {
      return false;
    }

    try {
      return !reference.window.closed;
    } catch {
      return false;
    }
  }

  /**
   * Hide trader panel in main workspace
   * Trader remains in detached window only
   *
   * @param traderId - Trader identifier
   * @returns true if trader was hidden, false if not found
   */
  hideTraderInMainWorkspace(traderId: string): boolean {
    if (!this.windowRegistry.has(traderId)) {
      return false;
    }

    this.hiddenTraders.add(traderId);
    this.persistWindowState();
    return true;
  }

  /**
   * Show trader panel in main workspace
   * Can be shown alongside detached window or by itself
   *
   * @param traderId - Trader identifier
   * @returns true if trader was shown, false if not found
   */
  showTraderInMainWorkspace(traderId: string): boolean {
    if (!this.windowRegistry.has(traderId) && this.hiddenTraders.has(traderId)) {
      this.hiddenTraders.delete(traderId);
      this.persistWindowState();
      return true;
    }

    if (this.hiddenTraders.has(traderId)) {
      this.hiddenTraders.delete(traderId);
      this.persistWindowState();
      return true;
    }

    return false;
  }

  /**
   * Check if trader is hidden in main workspace
   *
   * @param traderId - Trader identifier
   * @returns true if trader is hidden in main workspace
   */
  isHiddenInMainWorkspace(traderId: string): boolean {
    return this.hiddenTraders.has(traderId);
  }

  /**
   * Update sync status for a window
   *
   * @param traderId - Trader identifier
   * @param status - New sync status
   * @returns true if updated successfully
   */
  updateSyncStatus(traderId: string, status: SyncStatus): boolean {
    const reference = this.windowRegistry.get(traderId);

    if (!reference) {
      return false;
    }

    reference.syncStatus = status;
    return true;
  }

  /**
   * Get sync status for a window
   *
   * @param traderId - Trader identifier
   * @returns Sync status or null if window not found
   */
  getSyncStatus(traderId: string): SyncStatus | null {
    const reference = this.windowRegistry.get(traderId);
    return reference ? reference.syncStatus : null;
  }

  /**
   * Update window position
   *
   * @param traderId - Trader identifier
   * @param position - New position
   * @returns true if updated successfully
   */
  updateWindowPosition(traderId: string, position: WindowPosition): boolean {
    const reference = this.windowRegistry.get(traderId);

    if (!reference) {
      return false;
    }

    reference.position = position;

    try {
      reference.window.moveTo(position.x, position.y);
    } catch {
      // Window might not allow moving
    }

    this.persistWindowState();
    return true;
  }

  /**
   * Update window size
   *
   * @param traderId - Trader identifier
   * @param size - New size
   * @returns true if updated successfully
   */
  updateWindowSize(traderId: string, size: WindowSize): boolean {
    const reference = this.windowRegistry.get(traderId);

    if (!reference) {
      return false;
    }

    reference.size = size;

    try {
      reference.window.resizeTo(size.width, size.height);
    } catch {
      // Window might not allow resizing
    }

    this.persistWindowState();
    return true;
  }

  /**
   * Set focus on a window
   *
   * @param traderId - Trader identifier
   * @returns true if window was focused
   */
  focusTraderWindow(traderId: string): boolean {
    const reference = this.windowRegistry.get(traderId);

    if (!reference) {
      return false;
    }

    try {
      reference.window.focus();
      reference.isFocused = true;

      // Clear previous focus tracker
      if (this.focusTracker && this.focusTracker.timer) {
        clearTimeout(this.focusTracker.timer);
      }

      // Set new focus tracker
      this.focusTracker = { traderId, timer: null };

      // Emit focus event
      this.eventEmitter.emit('onWindowFocus', reference);

      return true;
    } catch {
      return false;
    }
  }

  /**
   * Register event listener for window events
   *
   * @param event - Event name: 'onWindowOpen', 'onWindowClose', 'onWindowFocus'
   * @param listener - Callback function
   */
  on(event: 'onWindowOpen' | 'onWindowClose' | 'onWindowFocus', listener: WindowEventListener): void {
    this.eventEmitter.on(event, listener);
  }

  /**
   * Unregister event listener
   *
   * @param event - Event name
   * @param listener - Callback function
   */
  off(event: 'onWindowOpen' | 'onWindowClose' | 'onWindowFocus', listener: WindowEventListener): void {
    this.eventEmitter.off(event, listener);
  }

  /**
   * Persist window state to localStorage
   */
  private persistWindowState(): void {
    try {
      const metadata: WindowMetadata[] = Array.from(this.windowRegistry.values()).map(ref => ({
        traderId: ref.traderId,
        type: ref.type,
        position: ref.position,
        size: ref.size,
        openedAt: ref.openedAt
      }));

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(metadata));
    } catch (error) {
      console.error('Failed to persist window state:', error);
    }
  }

  /**
   * Get all window metadata
   *
   * @returns Array of window metadata
   */
  getWindowMetadata(): WindowMetadata[] {
    return Array.from(this.windowRegistry.values()).map(ref => ({
      traderId: ref.traderId,
      type: ref.type,
      position: ref.position,
      size: ref.size,
      openedAt: ref.openedAt
    }));
  }

  /**
   * Close all open windows
   * Useful for cleanup during app shutdown
   */
  closeAllWindows(): void {
    const traderIds = Array.from(this.windowRegistry.keys());
    traderIds.forEach(traderId => {
      this.closeTraderWindow(traderId);
    });
  }

  /**
   * Get current state snapshot
   * Useful for debugging or testing
   */
  getState() {
    return {
      openWindows: this.getOpenWindows(),
      hiddenTraders: Array.from(this.hiddenTraders),
      windowMetadata: this.getWindowMetadata(),
      totalOpenWindows: this.windowRegistry.size,
      totalHiddenTraders: this.hiddenTraders.size
    };
  }

  /**
   * Clear all state and destroy instance
   * Primarily for testing
   */
  destroy(): void {
    this.closeAllWindows();
    this.stopCrashDetection();
    this.windowRegistry.clear();
    this.hiddenTraders.clear();
    this.eventEmitter.clear();

    if (this.focusTracker && this.focusTracker.timer) {
      clearTimeout(this.focusTracker.timer);
    }

    this.focusTracker = null;
  }
}

/**
 * Export singleton instance
 */
export const windowManager = WindowManager.getInstance();
